import { useState, useEffect } from 'react';
import {
  AnfrageCreate,
  anfragenApi,
  customersApi,
  type AnfrageDringlichkeit,
} from '@/lib/api';
import { CircleAlert } from 'lucide-react';
import {
  Button,
  Callout,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  NativeSelect,
  statusInfo,
  toast,
} from '@elbklar/ui';
import { TextareaField, TextField } from '@/components/form/Felder';
import { formatApiError } from '@/lib/validation';
import type { Customer } from '@/types';

interface AnfrageFormModalProps {
  /** Kunde, mit dem der Dialog aufgeht (Sprung aus der Kundenakte). */
  initialCustomerId?: string;
  onClose: () => void;
  onSave: () => void;
}

// Wörter aus STATUS (`dringlichkeit_…`), wie Pipeline und PM sie zeigen.
const DRINGLICHKEIT_OPTIONS: { value: AnfrageDringlichkeit; label: string }[] = (
  ['langfristig', 'normal', 'schnell', 'dringend', 'sofort'] as AnfrageDringlichkeit[]
).map((v) => ({ value: v, label: statusInfo(`dringlichkeit_${v}`).label }));

const LEAD_QUELLE_OPTIONS = [
  { value: '', label: 'Nicht angegeben' },
  { value: 'kaltakquise', label: 'Kaltakquise' },
  { value: 'empfehlung', label: 'Empfehlung' },
  { value: 'website', label: 'Website' },
  { value: 'messe', label: 'Messe' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export function AnfrageFormModal({ initialCustomerId, onClose, onSave }: AnfrageFormModalProps) {
  const [formData, setFormData] = useState<AnfrageCreate>({
    customer_id: initialCustomerId ?? '',
    value: undefined,
    dringlichkeit: 'normal',
    lead_quelle: undefined,
    sales_nachricht: '',
    kundenwunsch: '',
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      // Alle Seiten: mit einer Seite von 100 fehlte jeder Kunde ab dem 101.
      // in der Auswahl — neu angelegte zuerst (Befund 09.09.2026).
      setCustomers(await customersApi.listAll());
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.customer_id) {
      setError('Kunde fehlt');
      return;
    }

    try {
      setLoading(true);
      await anfragenApi.create({
        ...formData,
        status: 'NEU',
        sales_nachricht: formData.sales_nachricht || undefined,
        kundenwunsch: formData.kundenwunsch || undefined,
      });
      toast.success('Anfrage angelegt');
      onSave();
    } catch (err) {
      setError(formatApiError(err) || 'Anfrage ließ sich nicht speichern');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(offen) => !offen && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Anfrage anlegen</DialogTitle>
        </DialogHeader>

        {error && <Callout tone="danger" icon={CircleAlert} title={error} boxed />}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer */}
          <Combobox
            label="Kunde"
            required
            options={customers.map((c) => ({ value: c.id, label: c.company_name }))}
            value={formData.customer_id}
            onChange={(val) => setFormData({ ...formData, customer_id: val })}
            placeholder="Kunde suchen"
          />

          {/* Value */}
          <TextField
            label="Wert"
            type="number"
            kbd="EUR"
            value={formData.value ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                value: e.target.value ? parseInt(e.target.value) : undefined,
              })
            }
            placeholder="0"
            min="0"
          />

          {/* Dringlichkeit + Lead-Quelle */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Dringlichkeit">
              {(f) => (
                <NativeSelect
                  {...f}
                  value={formData.dringlichkeit || 'normal'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dringlichkeit: e.target.value as AnfrageDringlichkeit,
                    })
                  }
                  options={DRINGLICHKEIT_OPTIONS}
                />
              )}
            </Field>
            <Field label="Lead-Quelle">
              {(f) => (
                <NativeSelect
                  {...f}
                  value={formData.lead_quelle || ''}
                  onChange={(e) => setFormData({ ...formData, lead_quelle: e.target.value || undefined })}
                  options={LEAD_QUELLE_OPTIONS}
                />
              )}
            </Field>
          </div>

          {/* Kundenwunsch */}
          <TextareaField
            label="Kundenwunsch"
            value={formData.kundenwunsch || ''}
            onChange={(e) => setFormData({ ...formData, kundenwunsch: e.target.value })}
            rows={2}
            placeholder="Was möchte der Kunde?"
          />

          {/* Sales-Nachricht */}
          <TextareaField
            label="Notiz für den Vertrieb"
            value={formData.sales_nachricht || ''}
            onChange={(e) => setFormData({ ...formData, sales_nachricht: e.target.value })}
            rows={3}
            placeholder="Zusätzliche Informationen"
          />

          {/* Actions */}
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" loading={loading}>
              Anfrage anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
